import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import MainCalendar from '../components/calendar/MainCalendar';
import { Container, Typography } from '@mui/material';
import '../calendar.css';
import OfflineBanner from '../components/common/OfflineBanner';
import { apiHealthService } from '../services/apiHealthService';

const CalendarPage: React.FC = () => {
    const { isAuthenticated } = useAuth();
    const { businessId } = useBusinessTheme();
    const location = useLocation();
    const navigationState = location.state as { date?: string; appointmentId?: string } | null;
    const [imageLoaded, setImageLoaded] = useState<boolean | null>(null);

    // Build the full image URL using the API base URL
    const imageUrl = businessId ? `${apiHealthService.getApiUrl()}/api/images/${businessId}` : null;

    // Debug logging for business logo
    useEffect(() => {
        console.log('[Calendar] Business logo debug:', {
            businessId,
            imageUrl,
            apiBaseUrl: apiHealthService.getApiUrl()
        });

        if (imageUrl) {
            // Test if the image loads successfully
            const img = new Image();
            img.onload = () => {
                console.log('[Calendar] Business logo loaded successfully:', imageUrl);
                setImageLoaded(true);
            };
            img.onerror = (error) => {
                console.error('[Calendar] Business logo failed to load:', {
                    url: imageUrl,
                    error
                });
                setImageLoaded(false);
            };
            img.src = imageUrl;
        }
    }, [businessId, imageUrl]);

    if (!isAuthenticated) {
        return <Navigate to="/login" />;
    }

    return (
        <>
            <Helmet>
                <title>Calendar | MindShare Calendar</title>
            </Helmet>

            {/* Offline banner - only shown on calendar view */}
            <OfflineBanner />

            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    position: 'relative',
                    isolation: 'isolate',
                    backgroundColor: 'transparent',
                    height: { xs: 'calc(100vh - 55px)', sm: 'calc(100vh - 64px)' },
                    overflow: 'hidden',
                    '&::before': imageUrl ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `url(${imageUrl})`,
                        backgroundSize: '550px auto',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        opacity: 0.1,
                    } : {},
                }}
            >
                <MainCalendar 
                    initialDate={navigationState?.date ? new Date(navigationState.date) : undefined}
                    highlightAppointmentId={navigationState?.appointmentId}
                />
            </Container>
        </>
    );
};

export default CalendarPage; 